import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';

import { EnvironmentService } from 'src/app/services/environment.service';
import { Auth0UserModel } from '../models/auth0-user.model';
import { Auth0TokenRequestModel } from '../models/auth0-token.request-model';
import { Auth0TokenResponseModel } from '../models/auth0-token.response-model';
import { LoginCallbackState } from '../enums/login-callback-state.enum';
import { MonitoringService } from 'src/app/services/monitoring.service';


/**
 * We use Auth0 as a membership provider.
 * Login process:
 * https://auth0.com/docs/flows/authorization-code-flow
 * 1. we navigate to Auth0 Universal Login page: https://auth0.com/docs/universal-login
 * 2. user logs in there and being navigated back to our site with some parameters in url
 * 3. we fetch code from url, make another request to Auth0 using this code
 * and get access token from the response and keep it in local storage.
 *
 * We use Auth0 JWT package to manage access token (we send it in a request header with each API request)
 * and ID token (JWT token, which contains user info)
 *
 * The similar process implemented in Liferaft (the difference is that we get access token in callback url
 * and not a code which need to be exchanged to tokens in the next request).
 */

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly accessTokenCookie = 'jr-access-token';
  private readonly redirectToUrlCookie = 'jr-redirect-to';
  private readonly cookiePath = '/';
  private readonly scope: string = 'openid profile';
  private readonly state: string = 'abc';
  private get window(): Window {
    return this.document.defaultView;
  }

  /** define if user is authenticated */
  isLoggedIn$: BehaviorSubject<boolean>;
  /** returns Auth0 user */
  user$: BehaviorSubject<Auth0UserModel | undefined>;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private jwtHelper: JwtHelperService,
    private httpClient: HttpClient,
    private environmentService: EnvironmentService,
    private cookieService: CookieService,
    private monitoringService: MonitoringService
  ) {
    this.isLoggedIn$ = new BehaviorSubject(this.isLoggedIn);
    this.user$ = new BehaviorSubject(this.getUser());
  }

  private navigateExternal(url: string) {
    this.window.location.href = url;
  }

  private get isLoggedIn(): boolean {
    const accessToken = this.cookieService.get(this.accessTokenCookie);
    if (accessToken) {
      const isAccessTokenValid = this.jwtHelper.isTokenExpired(accessToken) ? false : true;
      return isAccessTokenValid ? true : false;
    } else {
      return false;
    }
  }

  /** Auth0 user */
  private getUser(): Auth0UserModel | undefined {
    const accessToken = this.cookieService.get(this.accessTokenCookie);
    if (!accessToken) {
      return undefined;
    }
    const decodedToken = this.jwtHelper.decodeToken(accessToken);
    if (decodedToken) {
      return {
        name: decodedToken['https://jayride.com/username'],
        email: decodedToken['https://jayride.com/Email'],
        picture: decodedToken['https://jayride.com/picture'] // user picture can be available when register with social accounts
      };
    } else {
      return undefined;
    }
  }

  /**
   * we assume that error code will be in square brackets
   * e.g. "unauthorized: [EMAIL_NOT_VERIFIED] - Please verify your email before logging in."
   */
  private getErrorCode(errMsg: string): LoginCallbackState {
    const regExp = /\[(.*?)\]/;
    const matches = regExp.exec(errMsg);
    return (matches && matches[1] ? matches[1] : errMsg) as LoginCallbackState;
  }

  /** https://auth0.com/docs/tokens/refresh-tokens/get-refresh-tokens */
  private async exchangeCodeForToken(code: string): Promise<string> {
    const url = `https://${this.environmentService.getAuth0Domain()}/oauth/token`;
    const data: Auth0TokenRequestModel = {
      grant_type: 'authorization_code',
      code: code,
      client_id: this.environmentService.getAuth0ClientId(),
      redirect_uri: this.window.location.href
    };
    const res = (await this.httpClient.post(url, data).toPromise()) as Auth0TokenResponseModel;
    return res.access_token;
  }

  private storeAccessToken(accessToken: string) {
    const cookieDomain = this.environmentService.getCookieDomain();
    // remove old cookie (if exists)
    this.cookieService.delete(this.redirectToUrlCookie, this.cookiePath, cookieDomain);
    // store cookie
    this.cookieService.set(this.accessTokenCookie, accessToken, undefined,this.cookiePath, cookieDomain);
    // check if cookie has been stored
    const savedToken = this.cookieService.get(this.accessTokenCookie);
    if (accessToken !== savedToken) {
      throw new Error(LoginCallbackState.CookiesDisabledError);
    }
  }

  cleanQueryParams() {
    this.window.history.replaceState({}, document.title, this.window.location.pathname);
  }

  /** 
   * handle response form Auth0 (query parameters) 
   * @returns login callback state code
   */
  async handleLoginCallback(): Promise<LoginCallbackState> {
    try {
      const urlParams = new URLSearchParams(this.window.location.search);

      // check if there is an error
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      if (error || errorDescription) {
        const errMsg = `${error || '[LOGIN_ERROR]'}: ${errorDescription}`;
        return Promise.resolve(this.getErrorCode(errMsg));
      }

      const view = urlParams.get('view');
      if (view) {
        return Promise.resolve(view as LoginCallbackState);
      }

      // e.g. https://YOUR_APP/callback?code=BPPLN3Z4qCTvSNOy
      const accessCode = urlParams.get('code');
      if (accessCode) {
        const accessToken = await this.exchangeCodeForToken(accessCode);
        if (accessToken) {
          this.cleanQueryParams();
          this.storeAccessToken(accessToken);
          this.checkUserState();

          if (!this.isLoggedIn) {
            return Promise.resolve(LoginCallbackState.InvalidAccessToken);
          }
        } else {
          return Promise.resolve(LoginCallbackState.NoAccessToken);
        }
      }
      return Promise.resolve(LoginCallbackState.NoErrors);
    } catch (err: any) {
      this.monitoringService.logError(err);

      if (err?.error?.error === 'invalid_grant') { // Auth0 error: attempt to reuse 'code'
        return Promise.resolve(LoginCallbackState.ExchangeCodeError);
      } else if (err?.message === LoginCallbackState.CookiesDisabledError) {
        return Promise.resolve(LoginCallbackState.CookiesDisabledError);
      }
      // throw err;
      return Promise.resolve(LoginCallbackState.GeneralLoginError);
    }
  }

  checkUserState() {
    // trigger new event if value has changed
    if (this.isLoggedIn$.value != this.isLoggedIn) {
      this.user$.next(this.getUser());
      this.isLoggedIn$.next(this.isLoggedIn);
    }
  }

  /** set a cookie with current URL to return return to it later after login/logout process */
  private setPageToReturn() {
    this.cookieService.set(this.redirectToUrlCookie, this.window.location.href, undefined, this.cookiePath, this.environmentService.getCookieDomain());
  }

  /** define a URL to return after login/logout process. 
   * If not set, it will use Homepage; After calling this function, the respective cookie will be deleted. */
  private getPageToReturn(): string {
    const redirectToUrl = this.cookieService.get(this.redirectToUrlCookie);
    this.cookieService.delete(this.redirectToUrlCookie, this.cookiePath, this.environmentService.getCookieDomain());
    return redirectToUrl || this.environmentService.getJpressUrl();
  }

  navigateToInitialPage() {
    const url = this.getPageToReturn();
    this.navigateExternal(url);
  }

  /** 
   * will navigate to Auth0, then back to login callback page, and then to a page where the login button was clicked
   * @param {boolean} isSignUp if true - the registration form will be displayed, otherwise (default) - login form
   * @param {string=} loginCallbackUrl URL, Auth0 will navigate after authentication process to process login response
   * @param {string=} langParam URL language param, e.g 'es'
   * @param {boolean=} isAgent default 'false', if 'true' - will send user type 'agent' query parameter to Auth0
   */
  logIn(isSignUp = false, loginCallbackUrl?: string, langParam?: string, isAgent = false) {
    this.setPageToReturn();
    const url: string = `https://${this.environmentService.getAuth0Domain()}/authorize?`
      + `${isSignUp ? '&screen_hint=signup' : ''}`
      + `&audience=${this.environmentService.getAuth0Audience()}`
      + `&scope=${this.scope}`
      + `&response_type=code` // will return a code which then need to be exchanged to tokens; if 'token' - will return token data in callback url
      + `&client_id=${this.environmentService.getAuth0ClientId()}`
      + `&redirect_uri=${loginCallbackUrl || this.window.location.href}` // login callback page
      // + `&nonce=${this.nonce}`
      + `&state=${this.state}`
      + `${langParam ? `&ui_locales=${langParam}` : ''}`
      + `${isAgent ? `&userType=agent` : ''}`;
    this.navigateExternal(url);
  }

  /** 
   * will navigate to Auth0, then to logout callback page and then to the page where the logout button was clicked 
   * https://auth0.com/docs/logout/redirect-users-after-logout
   */
  logOut(logoutCallbackUrl?: string) {
    this.setPageToReturn();
    this.cookieService.delete(this.accessTokenCookie, this.cookiePath, this.environmentService.getCookieDomain());
    this.checkUserState();
    const url = `https://${this.environmentService.getAuth0Domain()}/v2/logout?`
      + `returnTo=${logoutCallbackUrl || this.window.location.href}` // logout callback page
      + `&client_id=${this.environmentService.getAuth0ClientId()}`;
    this.navigateExternal(url);
  }
}
